import StatusPill from '@components/ui/StatusPill';
import { Button, Group, Progress, Text } from '@mantine/core';
import { CaretRightIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { type FC, useState } from 'react';

import { ColumnHeader, NumberCell } from './CardFigures';
import { COLUMNS, type ColumnKey, formatDue, fromStockFor } from './cardColumns';
import { remaining } from './orderTotals';
import { type SkuGroup } from './skuGroups';
import { useOrderDocument } from './useOrderDocument';

import styles from './JobCard.module.css';
import { formatCount } from '@utils/currency';

interface SkuCardProps {
    group: SkuGroup;
    /** Finished goods per SKU at this site: what is standing here, against the job. */
    stockByProduct: Map<string, { total: number }>;
    /** The order currently being acknowledged, so only its own button spins. */
    acknowledging: string | null;
    onAcknowledge: (line: SkuGroup['lines'][number]) => void;
    /** Record production for this product, against the orders that asked for it. */
    onDeclare: (group: SkuGroup) => void;
}

/**
 * One product, as a commitment rather than as a line item.
 *
 * THE SAME SEVEN FIGURES AS THE ORDER CARD, in the same columns, rendered by the same cells —
 * this is `JobCard` with the two levels swapped, not a second way of counting. A product row is
 * the sum of the order rows beneath it, by construction: both are read off the same lines.
 *
 * NO STATUS BADGE AT THIS LEVEL, on purpose. A SKU wanted by an ISSUED order and an
 * IN_PRODUCTION one has no single status, and a "mixed" pill would be a word that means nothing
 * on a factory floor. Status stays on the order rows, where it is true. The card still tints
 * itself when something underneath is unacknowledged, because that is a fact about the product's
 * whole position and wants seeing while collapsed.
 *
 * RECORD PRODUCTION IS AT THE CARD, THE OTHER TWO ACTIONS ARE NOT. A site that made 500 of
 * something should not have to decide which order it answers before writing the number down —
 * the declare grid takes every order wanting this SKU and picks the earliest for each row, which
 * the person can change. Order PDF and Acknowledge are irreducibly about one order, so they sit
 * on the order rows.
 *
 * DUE IS THE EARLIEST of the orders wanting it, because that is the one that decides what gets
 * made next. Each order's own date is one click away.
 */
const SkuCard: FC<SkuCardProps> = ({
    group,
    stockByProduct,
    acknowledging,
    onAcknowledge,
    onDeclare,
}) => {
    /*
     * COLLAPSED UNTIL ASKED, as the order card is. The card answers "how much of this do I owe";
     * which orders want it is the follow-up question.
     */
    const [isOpen, setIsOpen] = useState(false);
    const orderDocument = useOrderDocument();

    const onHand = stockByProduct.get(group.productId)?.total ?? 0;

    return (
        <div
            className={`${styles.card} ${
                group.needsAcknowledgement ? styles.unacknowledged : ''
            }`}
        >
            <ColumnHeader />

            <div className={styles.head}>
                {/* The whole head is the disclosure, not a chevron somebody has to aim at. */}
                <button
                    type="button"
                    className={styles.disclosure}
                    aria-expanded={isOpen}
                    onClick={() => setIsOpen((open) => !open)}
                >
                    <CaretRightIcon
                        size={14}
                        weight="bold"
                        className={isOpen ? styles.caretOpen : styles.caret}
                    />
                    <div className={styles.title}>
                        <Text fz={16} fw={600} c={neutralColors.textPrimary}>
                            {group.productName}
                        </Text>
                        <Text fz={12} c={neutralColors.textSecondary}>
                            {/* Earliest-due leads: it is what decides what gets made next. */}
                            Earliest due {formatDue(group.dueDate)} · {group.lines.length}{' '}
                            order{group.lines.length === 1 ? '' : 's'}
                            {onHand > 0 ? ` · ${formatCount(onHand)} on hand` : ''}
                        </Text>
                    </div>
                </button>

                <div className={styles.numbers}>
                    {COLUMNS.map((column) => (
                        <NumberCell
                            key={column.key}
                            value={group.totals[column.key]}
                            muted={column.dimWhenZero && group.totals[column.key] === 0}
                            hint={column.hint}
                            fromStock={group.lines.reduce(
                                (acc, entry) =>
                                    acc +
                                    fromStockFor(entry.line.allocatedFromStock, column.key),
                                0,
                            )}
                        />
                    ))}
                </div>

                <div className={styles.actions}>
                    {/*
                     * Hidden once the product owes nothing to make. A grid of zeroes invites
                     * declaring a run that was already recorded, and the card is already saying
                     * the work is done.
                     */}
                    {group.totals.open > 0 && (
                        <Button
                            variant={ButtonVariant.DEFAULT}
                            size="xs"
                            onClick={() => onDeclare(group)}
                        >
                            Record production
                        </Button>
                    )}
                </div>
            </div>

            {isOpen &&
                group.lines.map((entry) => {
                    const { line } = entry;
                    const over = line.produced > line.ordered;

                    /*
                     * Two segments, the same way the order card draws them: SENT is the order
                     * actually being answered, READY is what has been made and not yet shipped.
                     * Ready counts only what exceeds what has gone, so a unit made and then sent
                     * fills the bar once.
                     */
                    const sentPct =
                        line.ordered > 0
                            ? Math.min(100, (line.dispatched / line.ordered) * 100)
                            : 0;
                    const readyPct =
                        line.ordered > 0
                            ? Math.min(
                                  100 - sentPct,
                                  (Math.max(0, line.produced - line.dispatched) /
                                      line.ordered) *
                                      100,
                              )
                            : 0;

                    /* Coalesced, every field — see the order card: a cell handed undefined
                       renders a gap, which reads as "no data exists" rather than "this field
                       did not arrive" during a frontend/backend rollout window. */
                    const lineValues: Record<ColumnKey, number> = {
                        ordered: line.ordered ?? 0,
                        open: remaining(line.ordered, line.produced ?? 0),
                        manufactured: line.produced ?? 0,
                        qcApproved: line.qcApproved ?? 0,
                        qcPending: line.qcPending ?? 0,
                        sent: line.dispatched ?? 0,
                        toSend: remaining(line.ordered, line.dispatched),
                    };

                    return (
                        <div className={styles.line} key={entry.key}>
                            <div className={styles.title}>
                                <Group gap={8}>
                                    <Text fz={13} fw={600} c={neutralColors.textPrimary}>
                                        {entry.orderCode}
                                    </Text>
                                    {entry.needsAcknowledgement && (
                                        <StatusPill
                                            label="Not acknowledged"
                                            tone="warning"
                                        />
                                    )}
                                </Group>
                                <Text fz={12} c={neutralColors.textMuted}>
                                    due {formatDue(entry.dueDate)}
                                    {over && (
                                        // fz is set explicitly: a nested Text does not inherit
                                        // its parent's size, it falls back to the theme default.
                                        <Text
                                            component="span"
                                            fz={12}
                                            c={semanticColors.warning.dark}
                                        >
                                            {' '}
                                            · over by {line.produced - line.ordered}
                                        </Text>
                                    )}
                                </Text>
                                <Progress.Root size="xs" w={220}>
                                    <Progress.Section
                                        value={over ? 100 : sentPct}
                                        color={over ? 'yellow' : 'green'}
                                    />
                                    {!over && (
                                        // Lighter: made, but not yet gone.
                                        <Progress.Section value={readyPct} color="green.3" />
                                    )}
                                </Progress.Root>
                            </div>

                            <div className={styles.numbers}>
                                {COLUMNS.map((column) => (
                                    <NumberCell
                                        key={column.key}
                                        value={lineValues[column.key]}
                                        muted={
                                            column.dimWhenZero &&
                                            lineValues[column.key] === 0
                                        }
                                        hint={column.hint}
                                        fromStock={fromStockFor(
                                            line.allocatedFromStock,
                                            column.key,
                                        )}
                                    />
                                ))}
                            </div>

                            <div className={styles.actions}>
                                {/* Both actions belong to ONE order, which is why they are down
                                    here rather than on the product above. */}
                                {entry.hasDocument && entry.productionOrderId && (
                                    <Button
                                        variant={ButtonVariant.DEFAULT}
                                        size="xs"
                                        loading={
                                            orderDocument.openingId === entry.productionOrderId
                                        }
                                        onClick={() => void orderDocument.open(entry.productionOrderId)}
                                    >
                                        Order PDF
                                    </Button>
                                )}
                                {entry.needsAcknowledgement && (
                                    <Button
                                        variant={ButtonVariant.ACTION}
                                        size="xs"
                                        loading={
                                            acknowledging ===
                                            (entry.productionOrderId ?? entry.workOrderId)
                                        }
                                        onClick={() => onAcknowledge(entry)}
                                    >
                                        Acknowledge
                                    </Button>
                                )}
                                {/* Kept, though it can no longer be asked for here: a line
                                    already waiting on the brand should still say so. */}
                                {line.closeRequestedAt && (
                                    <StatusPill label="Close requested" tone="info" />
                                )}
                            </div>
                        </div>
                    );
                })}
        </div>
    );
};

export default SkuCard;
